import type { ReactNode } from 'react'
import { useMemo } from 'react'

import { faGavel } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { CellContext, ColumnDef } from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { styled, css } from 'styled-components'

import type { GetAuctionBidsResponse } from '@b-stock/auction-api-client'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  EmptyState,
  ClientTimeZoneDateTime,
  PrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

const StyledEmptyState = styled(EmptyState)`
  padding: 2rem;
`

interface IProps {
  data: GetAuctionBidsResponse
  currency: string
  listing: OrchestratedAuctionListingDetails
}

type CustomerType = {
  [key: string]: {
    bidAmount: number
    createdAt: string
    userBid: boolean
    bidsNum: number
    winning: boolean
    earliestBidAt: string
    bidderNumber: number
    bids: {
      bidAmount: number
      createdAt: string
    }[]
  }
}

const InfoWrapper = styled.div`
  padding-bottom: 40px;
  color: ${designColors.neutral.darkGray};
  border-bottom: 1px solid ${designColors.neutral.mediumGray};
`

const InfoText = styled.div`
  display: flex;
  margin-bottom: 22px;
  ${Typography.Body4}
  letter-spacing: unset;
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 0.85rem;
  margin-right: 8px;
`

const TableHeader = styled.h2`
  ${Typography.Subtitle1}
  color: ${designColors.neutral.black};
  margin: 24px 0;
`

const StyledTable = styled.table`
  width: 100%;
  border-spacing: 0;
  overflow: scroll;
  border: 1px solid ${designColors.neutral.mediumGray};
`

const Row = styled.tr<{ $noLastBorder?: boolean; $header?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr;
  grid-gap: 1rem;
  border-bottom: 1px solid ${designColors.neutral.mediumGray};
  padding: 16px 49px 16px 8px;
  &:last-child {
    border-bottom: ${(o) =>
      o.$noLastBorder ? 0 : `1px solid ${designColors.neutral.mediumGray}`};
  }
  ${(o) =>
    o.$header &&
    css`
      ${Typography.Subtitle2}
    `}
  &:first-child {
    background-color: ${(o) => o.$header && designColors.success.bg};
  }
  @media (max-width: 1200px) {
    grid-gap: 0.5rem;
  }
  @media ${Breakpoints.max.small} {
    padding: 12px 4px;
    ${Typography.Subtitle2}
  }
`

const BidAmount = styled.div`
  display: flex;
  gap: 0.25rem;
  white-space: nowrap;
  justify-content: flex-end;
`

const Description = styled.div`
  ${Typography.Subtitle2}
  color: ${designColors.neutral.darkGray};
  margin-top: 24px;
`

const getStatisticData = (
  data: GetAuctionBidsResponse,
  intl: IntlShape,
  currency: string,
  listing: OrchestratedAuctionListingDetails
) => {
  const bidders: CustomerType = {}

  const cap = listing.auction.winningBidAmount
  const normalizedBids =
    'uniqueBidders' in data
      ? data.bids
      : data.bids.map((bid) => ({
          uniqueId: bid.accountId,
          // The full bids response (returned to service accounts) exposes each
          // bidder's true max (proxy) amount; mask it down to the winning bid to
          // match the limited, buyer-facing view.
          bidAmount: cap != null ? Math.min(bid.bidAmount, cap) : bid.bidAmount,
          createdAt: bid.createdAt,
          winning: false,
          userBid: false,
        }))

  normalizedBids.forEach((bid) => {
    bidders[bid.uniqueId] ??= {
      bidAmount: 0,
      createdAt: '',
      userBid: bid.userBid,
      bidsNum: 0,
      winning: bid.winning,
      earliestBidAt: bid.createdAt,
      bidderNumber: 0,
      bids: [],
    }

    // SAFETY: We just initialized bidders[bid.uniqueId] above, so it's guaranteed to exist
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const bidder = bidders[bid.uniqueId]!

    // Update the bid information
    bidder.bidsNum += 1
    bidder.winning = bidder.winning || bid.winning
    if (bid.createdAt < bidder.earliestBidAt) {
      bidder.earliestBidAt = bid.createdAt
    }
    bidder.bids.push({
      bidAmount: bid.bidAmount,
      createdAt: bid.createdAt,
    })
  })

  // Update each bidder's most recent bid and other properties
  Object.keys(bidders).forEach((bidderId) => {
    // SAFETY: Object.keys() returns keys that exist in the object
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const bidder = bidders[bidderId]!
    const sortedBids = bidder.bids.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    // SAFETY: bidder.bids is populated in the forEach loop above, so it's guaranteed to have at least one element
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const last = sortedBids[0]!
    bidder.bidAmount = last.bidAmount
    bidder.createdAt = last.createdAt
  })

  // Assign bidder numbers chronologically (first bidder = 1, second = 2, etc.)
  Object.entries(bidders)
    .sort(
      ([, a], [, b]) =>
        new Date(a.earliestBidAt).getTime() -
        new Date(b.earliestBidAt).getTime()
    )
    .forEach(([id], index) => {
      // SAFETY: id comes from Object.entries(bidders) so it's guaranteed to exist
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      bidders[id]!.bidderNumber = index + 1
    })

  return Object.values(bidders)
    .sort((a, b) => (a.winning ? -1 : b.bidAmount - a.bidAmount))
    .map((bid) => ({
      bidAmount: (
        <BidAmount>
          <span>
            <PrecisionNumberFormat
              value={bid.bidAmount}
              format={
                listing.contract?.bidBasis === 'RELATIVE'
                  ? 'percent'
                  : 'currency'
              }
              currency={currency}
              precision={listing.auction.bidPrecision}
              precisionStored={1}
            />
          </span>
          {listing.contract ? (
            <span>
              <FormattedMessage
                id={`Auction.statisticModal.bidAmount.${
                  listing.contract.bidBasis
                }`}
                values={{
                  unit: (
                    <FormattedMessage
                      id={`Contract.bidUnits.${listing.contract.bidUnit ?? 'ITEMS'}`}
                    />
                  ),
                }}
              />
            </span>
          ) : null}
        </BidAmount>
      ),
      createdAt: <ClientTimeZoneDateTime dateTime={parseISO(bid.createdAt)} />,
      bidsNum: bid.bidsNum,
      bidder: bid.userBid
        ? intl.formatMessage({
            id: 'Auction.statisticModal.you',
          })
        : intl.formatMessage(
            {
              id: 'Auction.statisticModal.bidder',
            },
            { position: bid.bidderNumber }
          ),
    }))
}

type BidRow = {
  bidder: string
  bidAmount: ReactNode
  createdAt: ReactNode
  bidsNum: number
}

export const StatisticModalContent = ({ data, currency, listing }: IProps) => {
  const intl = useIntl()

  const rightAlign = ({ getValue }: CellContext<BidRow, unknown>) => (
    <div style={{ textAlign: 'right' }}>{getValue() as ReactNode}</div>
  )

  const tableColumns = useMemo(
    (): ColumnDef<BidRow>[] => [
      {
        header: intl.formatMessage({
          id: 'Auction.statisticModal.column.bidder',
        }),
        accessorKey: 'bidder',
      },
      {
        header: intl.formatMessage({
          id: 'Auction.statisticModal.column.date',
        }),
        accessorKey: 'createdAt',
      },
      {
        header: () => (
          <div style={{ textAlign: 'right' }}>
            {intl.formatMessage({
              id: 'Auction.statisticModal.column.bids',
            })}
          </div>
        ),
        accessorKey: 'bidsNum',
        cell: rightAlign,
      },
      {
        header: () => (
          <div style={{ textAlign: 'right' }}>
            {intl.formatMessage({
              id: 'Auction.statisticModal.column.amount',
            })}
          </div>
        ),
        accessorKey: 'bidAmount',
        cell: rightAlign,
      },
    ],
    [intl]
  )
  const tableData = useMemo(
    () => getStatisticData(data, intl, currency, listing),
    [currency, data, intl, listing]
  )

  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable<BidRow>({
    columns: tableColumns,
    data: tableData,
    // v8's default cell renders `String(getValue())`. Columns like `createdAt`
    // store React nodes, so the default must render them directly.
    defaultColumn: {
      cell: ({ getValue }: CellContext<BidRow, unknown>) =>
        getValue() as ReactNode,
    },
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div>
      <InfoWrapper>
        <InfoText>
          <StyledIcon icon={faGavel} />
          <FormattedMessage
            id="Auction.details.statsBar.uniqueBidders"
            values={{
              bids: 'numberOfBids' in data ? data.numberOfBids : data.total,
              bidders:
                'uniqueBidders' in data
                  ? data.uniqueBidders
                  : new Set(data.bids.map((bid) => bid.accountId)).size,
            }}
          />
        </InfoText>
      </InfoWrapper>
      <TableHeader>
        <FormattedMessage id="Auction.statisticModal.tableTitle" />
      </TableHeader>
      <StyledTable>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Row key={headerGroup.id} $header>
              {headerGroup.headers.map((header) => (
                <td key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </td>
              ))}
            </Row>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <Row key={row.id} $noLastBorder>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  <div>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                </td>
              ))}
            </Row>
          ))}
        </tbody>
      </StyledTable>
      {!('numberOfBids' in data ? data.numberOfBids : data.total) ? (
        <StyledEmptyState
          icon={faGavel}
          size={2}
          title={<FormattedMessage id="Auction.statisticModal.noBidders" />}
        />
      ) : null}
      <Description>
        <FormattedMessage id="Auction.statisticModal.description" />
      </Description>
    </div>
  )
}
